import { Component, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { NgxPermissionsService } from 'ngx-permissions';
import { ConfirmationService, MessageService, PrimeIcons } from 'primeng/api';
import { lastValueFrom, map } from 'rxjs';
import {
  FinancialControllerService,
  Gateway,
  PageableRequest,
  PaymentResponseMessage,
  PaymentResponseMessageDetail,
  PaymentResponseMessageType
} from 'src/app/admin-api';
import {
  PageContent,
  TableActionButton,
  TableActionEvent,
  TableColumn,
  TableComponent
} from 'src/app/components/table';
import { Role, roleAsObject } from 'src/app/models';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

@Component({
  selector: 'app-payment-response-message',
  templateUrl: './payment-response-message.component.html',
  styleUrl: './payment-response-message.component.scss'
})
export class PaymentResponseMessageComponent implements OnInit {
  @ViewChild(TableComponent)
  table: TableComponent | undefined;

  permission = false;
  paymentResponseTypes: Array<PaymentResponseMessageType>;
  gateways: Array<Gateway>;
  currentResponseMessage: PaymentResponseMessage | undefined;

  cols = [
    new TableColumn.Builder()
      .setHeader('Id')
      .setField('paymentResponseMessageId')
      .setType('number')
      .setRouterLink('/subscription-settings/payment-response-message/')
      .setRouterLinkFieldName('paymentResponseMessageId')
      .build(),
    new TableColumn.Builder()
      .setHeader('Código')
      .setField('code')
      .setType('text')
      .setRouterLink('/subscription-settings/payment-response-message/')
      .setRouterLinkFieldName('paymentResponseMessageId')
      .build(),
    new TableColumn.Builder()
      .setHeader('Gateway')
      .setField('gatewayName')
      .setType('number')
      .setCondition('in')
      .build(),
    new TableColumn.Builder()
      .setHeader('Tipo de Resposta')
      .setField('typeContent')
      .setType('number')
      .setCondition('in')
      .setRouterLink('/subscription-settings/payment-response-message-type/')
      .setRouterLinkFieldName('paymentResponseMessageTypeId')
      .build()
  ];
  actionButtons: Array<TableActionButton> = [
    new TableActionButton(
      '',
      'Excluir',
      PrimeIcons.TRASH,
      undefined,
      '',
      'Excluir',
      'bottom',
      true,
      true,
      'danger',
      'small'
    )
  ];
  dropdownFilters:
    | { [key: string]: Array<{ label: string; value: string }> }
    | undefined;

  constructor(
    private title: Title,
    public financialService: FinancialControllerService,
    private permissionsService: NgxPermissionsService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {
    this.title.setTitle('Códigos de erro de cobrança');
  }

  async ngOnInit(): Promise<void> {
    this.permission = await this.permissionsService.hasPermission([
      roleAsObject(Role.Full_Administrator).enumValue
    ]);
    try {
      const [typeContent, gatewayName] = await Promise.all([
        this.getResponseTypes(),
        this.getGateways()
      ]);
      if (typeContent && gatewayName) {
        this.dropdownFilters = {
          typeContent: typeContent,
          gatewayName: gatewayName
        };
      }
      if (this.permission) {
        this.cols = [
          ...this.cols,
          new TableColumn('Ação', '', false, 'button')
        ];
      }
    } catch (error: any) {
      AppDialogService.showErrorDialog(error);
    }
  }

  async findPage(
    request: PageableRequest,
    service: unknown
  ): Promise<PageContent<PaymentResponseMessageDetail>> {
    try {
      const gatewayFilter = request.filters?.find(
        (f) => f.field === 'gatewayName'
      );
      if (
        gatewayFilter &&
        gatewayFilter.value &&
        Array.isArray(gatewayFilter.value) &&
        gatewayFilter.value.length > 0
      ) {
        request.filters.push({
          condition: 'in',
          field: 'gatewayId',
          value: gatewayFilter.value,
          fieldType: 'number'
        });
      }
      const typeFilter = request.filters?.find(
        (f) => f.field === 'typeContent'
      );
      if (
        typeFilter &&
        typeFilter.value &&
        Array.isArray(typeFilter.value) &&
        typeFilter.value.length > 0
      ) {
        request.filters.push({
          condition: 'in',
          field: 'paymentResponseMessageTypeId',
          value: typeFilter.value,
          fieldType: 'number'
        });
      }

      return lastValueFrom(
        (service as FinancialControllerService)
          .listPaymentResponseMessagesDetailed(request)
          .pipe(
            map(
              (data) => data.result as PageContent<PaymentResponseMessageDetail>
            )
          )
      );
    } catch (error: any) {
      AppDialogService.showErrorDialog(error);
    }
  }

  async getResponseTypes(): Promise<
    Array<{
      label: string;
      value: string;
    }>
  > {
    const objetos: Array<{ label: string; value: string }> = [];
    this.paymentResponseTypes = await lastValueFrom(
      this.financialService
        .findAllPaymentResponseMessageTypes()
        .pipe(map((data) => data.result))
    );
    for (let i = 0; i < this.paymentResponseTypes.length; i++) {
      objetos.push({
        label: this.paymentResponseTypes[i].typeContent,
        value:
          this.paymentResponseTypes[i].paymentResponseMessageTypeId.toString()
      });
    }
    return objetos;
  }

  async getGateways(): Promise<
    Array<{
      label: string;
      value: string;
    }>
  > {
    const objetos: Array<{ label: string; value: string }> = [];
    this.gateways = await lastValueFrom(
      this.financialService.findGatewaysList().pipe(map((data) => data.result))
    );
    for (let i = 0; i < this.gateways.length; i++) {
      objetos.push({
        label: this.gateways[i].gatewayId + '. ' + this.gateways[i].gatewayName,
        value: this.gateways[i].gatewayId.toString()
      });
    }
    return objetos;
  }

  async deletePaymentResponseMessage(
    paymentResponseMessage: PaymentResponseMessage
  ) {
    LoaderService.showLoader();
    try {
      const result = await lastValueFrom(
        this.financialService
          .deleteResponseMessage(
            paymentResponseMessage.paymentResponseMessageId
          )
          .pipe(map((data) => data.result))
      );
      this.messageService.add({
        severity: 'success',
        detail: result,
        summary: 'Sucesso'
      });
      this.table?.refresh();
      delete this.currentResponseMessage;
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    }
    LoaderService.showLoader(false);
  }
  async buttonClick(event: TableActionEvent) {
    this.confirmationService.confirm({
      header: 'Reprovação',
      message: 'Tem certeza que deseja excluir?',
      acceptLabel: 'Sim',
      rejectLabel: 'Não',
      accept: async () => {
        await this.deletePaymentResponseMessage(event.item);
      },
      target: event.$event.target
    });
  }
}
